import React, { useEffect, useState } from 'react';
import { FaAngleLeft } from 'react-icons/fa6';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchWallets, getFormattedAmount, swapCurrency } from '../../../actions/payments';
import './swapCurrency.css';
import '../Transactions/transactions.css';
import { refreshUserDetails } from '../../../actions/user';
import { Modal } from 'bootstrap';
import TransactionPinModal from '../../../components/TransactionPinModal'; // adjust path if needed
import { handleValidationError } from '../../../actions/validation';

const SwapCurrency = ({ userDetails }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userWallets, setUserWallets] = useState([]);
  const swapDetails = location.state?.converterDetails;
  const exchangeOverview = location.state?.exchangeOverview;
  const hasSetPin = userDetails?.has_set_pin || false;

  const { currencyOptions } = useSelector((state) => state.country);  

  const getUserWalletInfoByCurrencyCode = (code) => {
    return userWallets?.find(c => c.currency === code) || {};
  };
  const getCurrencyInfoByCurrencyCode = (code) => {
    return currencyOptions?.find(c => c.currencyCode === code) || {};
  };

  const getFlagUrl = (countryCode) => {
    if (!countryCode) return "";
    return `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;
  };

  useEffect(() => {
    if (!swapDetails) {
      navigate(-1);
    }
  }, [swapDetails, navigate]);
  

  const { sendCurrency, receiveCurrency, sendAmount, receiveAmount, exchangeRate } = swapDetails || {};

  const sendWalletInfo = getUserWalletInfoByCurrencyCode(sendCurrency);
  const sendInfo = getCurrencyInfoByCurrencyCode(sendCurrency);
  const receiveInfo = getCurrencyInfoByCurrencyCode(receiveCurrency);  

  const fetchAllWallets = async () => {
      setLoading(true);
      try {
          const response = await fetchWallets();
          await refreshUserDetails(dispatch);
          
          const wallets = userDetails?.wallet;
          
          setUserWallets(wallets);
      } catch (error) {
          console.error("Error fetching wallets:", error);
      } finally {
          setLoading(false);
      }
  };

  useEffect(() => {
    fetchAllWallets();
  }, []);

  // Receives the PIN directly from TransactionPinModal
  const handleConfirmPayment = async (pin) => {
    if (!pin || pin.length !== 4) {
      toast.error("Kindly enter your transfer PIN");
      return;
    }

    const credentials = {
      amount: sendAmount,
      to_currency: receiveCurrency,
      from_currency: sendCurrency,
      session_id: exchangeOverview?.session_id,
      pin,
    };
    
    setIsLoading(true);
    try {
      const response = await swapCurrency(credentials);
  
      setIsLoading(false);      
      
      toast.success("Currency swapped/converted successfully!");

      fetchAllWallets();
      await refreshUserDetails(dispatch);

      setTimeout(() => {
        navigate('/user/currency_swap/success', {
          state: {
            swapDetails,
            transaction: response?.data?.transaction || response?.data || response?.transaction || response
          }
        });
      }, 2000);
    } catch (error) {
      setIsLoading(false);
      handleValidationError(error, toast)
    }
  };

  const handleGoBack = () => {
    const backToCalDefault = {
      sendCurrency: sendCurrency,
      receiveCurrency: receiveCurrency,
      sendAmount: sendAmount,
      receiveAmount: receiveAmount,
    };
    navigate('/user/converter', { state: { backToCalDefault } });
  };

  return (
    <section className="main p-2 cards__page">
        <div className="d-flex align-items-center mb-4 pt-4">
          <button onClick={handleGoBack} className="back-btn-modern">
            <FaAngleLeft size={16} />
            <span>Go back</span>
          </button>
        </div>
        <div className="container py-4" style={{ maxWidth: '500px' }}>

          <div className="card p-4 shadow-sm rounded-4 border-0" style={{ backgroundColor: '#ffffff' }}>
            <h5 className="text-center mb-4 fw-bold" style={{ color: '#13053A', fontSize: '18px' }}>Review Your Swap</h5>

            <div className="d-flex flex-column gap-3 mb-4">
              {/* You're Sending Line */}
              <div className="d-flex align-items-center justify-content-between p-3 rounded-3" style={{ backgroundColor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                <div className="d-flex align-items-center gap-3">
                  <img src={getFlagUrl(sendInfo.countryCode)} alt="Send Flag" className="rounded-1 shadow-sm" width={36} height={24} style={{ objectFit: 'cover' }} />
                  <div className="text-start">
                    <span className="d-block text-muted" style={{ fontSize: '12px', fontWeight: '500' }}>You're sending</span>
                    <span className="fw-bold" style={{ color: '#1E293B', fontSize: '15px' }}>{sendInfo.symbol || ""}{getFormattedAmount(sendAmount)} <span className="text-muted" style={{ fontSize: '13px' }}>({sendCurrency})</span></span>
                  </div>
                </div>
              </div>

              {/* You'll Receive Line */}
              <div className="d-flex align-items-center justify-content-between p-3 rounded-3" style={{ backgroundColor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                <div className="d-flex align-items-center gap-3">
                  <img src={getFlagUrl(receiveInfo.countryCode)} alt="Receive Flag" className="rounded-1 shadow-sm" width={36} height={24} style={{ objectFit: 'cover' }} />
                  <div className="text-start">
                    <span className="d-block text-muted" style={{ fontSize: '12px', fontWeight: '500' }}>You'll receive</span>
                    <span className="fw-bold" style={{ color: '#10B981', fontSize: '16px' }}>{receiveInfo.symbol || ""}{getFormattedAmount(receiveAmount)} <span className="text-muted" style={{ fontSize: '13px' }}>({receiveCurrency})</span></span>
                  </div>
                </div>
              </div>

              {/* Exchange Rate Line */}
              <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom" style={{ borderColor: '#F1F5F9' }}>
                <span className="text-muted" style={{ fontSize: '13px', fontWeight: '500' }}>Exchange Rate</span>
                <span className="fw-semibold" style={{ color: '#1E293B', fontSize: '14px' }}>1 {sendCurrency} = {exchangeRate} {receiveCurrency}</span>
              </div>

              {/* Transaction Fee Line */}
              <div className="d-flex align-items-center justify-content-between px-3 py-2">
                <span className="text-muted" style={{ fontSize: '13px', fontWeight: '500' }}>Transaction Fee</span>
                <span className="fw-semibold" style={{ color: '#1E293B', fontSize: '14px' }}>{sendCurrency} {exchangeOverview?.fee || 0}</span>
              </div>
            </div>

            <button 
              className="btn primary__btn w-100 mt-2" 
              disabled={loading || isLoading}
              onClick={() => {
                if (!hasSetPin) {
                  toast.error("You need to set a transaction PIN before making transactions.");
                  const pinModal = new Modal(document.getElementById('transactionPinModal'));
                  pinModal.show();
                } else {
                  const payModal = new Modal(document.getElementById('payModal'));
                  payModal.show();
                }
              }}
            >
              {/* Confirm Swap */}
              {isLoading ? (
                <div className="spinner-border spinner-border-sm text-light" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                "Confirm Swap"
              )}
            </button>

            {/* New PIN Modal – replaces the old payModal */}
            <TransactionPinModal
              id="payModal"
              onSubmit={handleConfirmPayment}
              title="Confirm Currency Swap"
              description="Please enter your 4-digit PIN to complete this transaction."
            />
          </div>
        </div>
    </section>
  );
};

export default SwapCurrency;